import React from 'react';
import styled, { css } from 'styled-components';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
}

const StyledButton = styled.button<ButtonProps>`
  /* Base Styles */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-weight: ${({ theme }) => theme.typography.button.fontWeight};
  border-radius: ${({ theme }) => theme.shape.borderRadius.medium};
  transition: background-color 0.2s, box-shadow 0.2s, opacity 0.2s;
  
  /* Variant Styles */
  ${({ theme, variant = 'primary' }) => {
    switch (variant) {
      case 'secondary':
        return css`
          background-color: ${theme.colors.secondary.main};
          color: ${theme.colors.text.contrast};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.secondary.dark};
          }
        `;
      case 'danger':
        return css`
          background-color: ${theme.colors.error.main};
          color: ${theme.colors.text.contrast};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.error.dark};
          }
        `;
      case 'primary':
      default:
        return css`
          background-color: ${theme.colors.primary.main};
          color: ${theme.colors.text.contrast};
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primary.dark};
          }
        `;
    }
  }}
  
  /* Size Styles */
  ${({ theme, size = 'medium' }) => {
    switch (size) {
      case 'small':
        return css`
          padding: ${theme.spacing(0.75)} ${theme.spacing(1.5)};
          font-size: ${theme.typography.body2.fontSize};
        `;
      case 'large':
        return css`
          padding: ${theme.spacing(1.25)} ${theme.spacing(2.5)};
          font-size: ${theme.typography.h4.fontSize};
        `;
      case 'medium':
      default:
        return css`
          padding: ${theme.spacing(1)} ${theme.spacing(2)};
          font-size: ${theme.typography.body1.fontSize};
        `;
    }
  }}
  
  /* Full Width Style */
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}
  
  /* Disabled State */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Focus State */
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary.light}30; /* 30% opacity */
  }
`;

export const Button: React.FC<ButtonProps> = ({ children, ...props }) => {
  return <StyledButton {...props}>{children}</StyledButton>;
};

export default Button; 